import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../software/api";
import { FileText, ScrollText, Wrench, ShoppingCart, Search, ChevronLeft, ChevronRight, User } from "lucide-react";

const PAGE = 50;

const TYPES = [
  { key: "invoices",   label: "Invoices",     icon: FileText,     accent: "#123a63", tile: "bg-[#123a63]" },
  { key: "quotes",     label: "Quotes",       icon: ScrollText,   accent: "#b45309", tile: "bg-[#b45309]" },
  { key: "workorders", label: "Work Orders",  icon: Wrench,       accent: "#166534", tile: "bg-[#166534]" },
  { key: "pos",        label: "POS Sales",    icon: ShoppingCart, accent: "#7c2d12", tile: "bg-[#7c2d12]" },
];

const fmtDate = (d) => {
  if (!d) return "—";
  const s = String(d).slice(0, 10);
  const p = s.split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s;
};
const money = (n) => (n === null || n === undefined || n === "" ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function StatusPill({ text, tone }) {
  const map = {
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
    red: "bg-red-100 text-red-800 border-red-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    slate: "bg-slate-100 text-slate-600 border-slate-300",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-bold ${map[tone] || map.slate}`}>{text}</span>;
}

export default function Transactions({ onOpenClient }) {
  const [kind, setKind] = useState("invoices");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});

  useEffect(() => { const t = setTimeout(() => setDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(0); }, [kind, debounced]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api("GET", `/legacy/browse/${kind}?q=${encodeURIComponent(debounced)}&skip=${page * PAGE}&limit=${PAGE}`);
      setData(d);
      if (!debounced) setCounts((c) => ({ ...c, [kind]: d.total }));
    } catch (e) { setData({ items: [], total: 0 }); }
    setLoading(false);
  }, [kind, debounced, page]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    TYPES.forEach((t) => {
      api("GET", `/legacy/browse/${t.key}?limit=1`).then((d) => setCounts((c) => ({ ...c, [t.key]: d.total }))).catch(() => {});
    });
  }, []);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE));
  const jump = (r) => { if (r.client_id) onOpenClient(r.client_id); };

  const cell = (v, cls = "truncate") => <span className={cls}>{v}</span>;
  const rowGrid = (cols) => ({ display: "grid", gridTemplateColumns: cols, gap: "0.5rem" });

  const CONFIG = {
    invoices: {
      cols: "90px 1fr 100px 100px 110px 130px",
      head: ["Date", "Client", "Nett", "Balance", "Salesperson", "Status"],
      render: (r) => {
        const owes = Number(r.balance_due) > 0.005;
        return [
          cell(fmtDate(r.date)),
          cell(r.client_name || "—", "truncate font-semibold text-slate-800"),
          cell(money(r.nett), "text-right"),
          cell(money(r.balance_due), `text-right ${owes ? "text-red-600 font-bold" : "text-slate-400"}`),
          cell(r.salesperson || "—"),
          <StatusPill text={owes ? "OWES" : "PAID"} tone={owes ? "red" : "green"} />,
        ];
      },
    },
    quotes: {
      cols: "90px 1fr 110px 110px 120px",
      head: ["Date", "Client", "Amount", "Salesperson", "Status"],
      render: (r) => {
        const active = String(r.active).toLowerCase() === "active";
        return [
          cell(fmtDate(r.date)),
          cell(r.client_name || "—", "truncate font-semibold text-slate-800"),
          cell(money(r.amount || r.gross), "text-right"),
          cell(r.salesperson || "—"),
          <StatusPill text={r.status || (active ? "Active" : "—")} tone={active ? "amber" : "slate"} />,
        ];
      },
    },
    workorders: {
      cols: "90px 1fr 1.6fr 120px 150px",
      head: ["Booked", "Client", "Issue", "Technician", "Status"],
      render: (r) => {
        const done = String(r.status || "").toLowerCase().includes("complet");
        return [
          cell(fmtDate(r.booked)),
          cell(r.client_name || "—", "truncate font-semibold text-slate-800"),
          cell(r.issue || r.type || "—"),
          cell(r.carried_out_by || r.taken_by || "—"),
          <StatusPill text={r.status || "Open"} tone={done ? "green" : "amber"} />,
        ];
      },
    },
    pos: {
      cols: "100px 1fr 130px 140px 110px",
      head: ["Date", "Client / Sale", "Payment", "Clerk", "Amount"],
      render: (r) => [
        cell(fmtDate(r.date)),
        cell(r.client_name || r.sale_type || "—", "truncate font-semibold text-slate-800"),
        cell(r.payment_method || r.sale_type || "—"),
        cell(r.employee || "—"),
        cell(money(r.amount), "text-right font-bold"),
      ],
    },
  };
  const cfg = CONFIG[kind];
  const active = TYPES.find((t) => t.key === kind);

  return (
    <div data-testid="transactions-browser">
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Transaction History</h1>
      <p className="text-sm text-slate-500 mb-5">Every invoice, quote, work order and counter sale — 15 years of records. Tap any row to open that client.</p>

      {/* Big colour-coded type tiles — recognise, don't recall */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {TYPES.map((t) => {
          const on = t.key === kind;
          return (
            <button key={t.key} onClick={() => setKind(t.key)} data-testid={`txn-tab-${t.key}`}
              className={`flex items-center gap-3 rounded-2xl p-4 text-left transition-all border-2 ${on ? `${t.tile} text-white border-transparent shadow-lg` : "bg-white text-slate-800 border-slate-200 hover:border-slate-400"}`}>
              <span className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${on ? "bg-white/20" : "bg-slate-100"}`}>
                <t.icon className={`h-6 w-6 ${on ? "text-white" : "text-slate-500"}`} />
              </span>
              <span className="min-w-0">
                <span className="block font-display font-black text-lg leading-tight">{t.label}</span>
                <span className={`block text-[13px] font-semibold ${on ? "text-white/80" : "text-slate-400"}`}>
                  {counts[t.key] !== undefined ? `${counts[t.key].toLocaleString()} records` : "…"}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Type-and-go search */}
      <div className="relative max-w-lg mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="txn-search"
          placeholder="Search client name, phone or record number…"
          className="w-full rounded-xl border-2 border-slate-200 pl-10 pr-3 py-3 text-base focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none" />
      </div>

      <p className="text-xs text-slate-400 mb-2" data-testid="txn-result-count">
        {loading ? "Loading…" : <>{data.total.toLocaleString()} {active.label} {debounced ? "match your search" : "on record"} · showing {data.items.length}</>}
      </p>

      {/* List */}
      <div className="border-2 border-slate-200 rounded-2xl overflow-hidden bg-white">
        <div className="px-4 py-2 text-white text-[11px] font-black uppercase tracking-wide" style={{ ...rowGrid(cfg.cols), backgroundColor: active.accent }}>
          {cfg.head.map((h, i) => <span key={i} className={i >= cfg.head.length - 2 && kind !== "pos" ? "" : ""}>{h}</span>)}
        </div>
        {data.items.length === 0 && !loading && (
          <p className="p-6 text-center text-slate-400 text-sm" data-testid="txn-empty">No {active.label.toLowerCase()} found{debounced ? " for this search" : ""}.</p>
        )}
        {data.items.map((r, i) => (
          <button key={`${r.legacy_id}-${i}`} onClick={() => jump(r)} disabled={!r.client_id}
            data-testid="txn-row"
            className={`w-full text-left px-4 py-2.5 text-[13px] border-b border-slate-100 items-center transition-colors ${r.client_id ? "hover:bg-sky-50 cursor-pointer" : "cursor-default opacity-80"}`}
            style={rowGrid(cfg.cols)}>
            {cfg.render(r).map((c, j) => <React.Fragment key={j}>{c}</React.Fragment>)}
          </button>
        ))}
      </div>

      {/* Pagination */}
      {data.total > PAGE && (
        <div className="flex items-center justify-between mt-4">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="txn-prev"
            className="inline-flex items-center gap-1 rounded-full border-2 border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40 hover:border-slate-400">
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>
          <span className="text-sm font-semibold text-slate-500" data-testid="txn-page">Page {page + 1} of {totalPages.toLocaleString()}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="txn-next"
            className="inline-flex items-center gap-1 rounded-full border-2 border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40 hover:border-slate-400">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
